<template>
    <div>
        <div class="gradient-box gradient-box-peach" />
        <div class="row" data-test-view="skills-list">
            <div class="col-md-2 hidden-xs hidden-sm">
                <div class="profile-column">
                    <l-side-menu-picture :src="icon"></l-side-menu-picture>
                    <p v-for="item in $t('skills.headers')" :key="item">
                        <l-text :text="item"></l-text>
                    </p>
                </div>
            </div>

            <div class="col-md-10">
                <l-card-content>
                    <l-headline :title="$t('skills.title')" :tabs="tabs" btn-test-id="skill-create">
                    </l-headline>

                    <div class="row">
                        <div class="col-md-3">
                            <l-select
                                v-model="teamFilter"
                                wrapper-class="dropdown"
                                :options="[
                                    { value: ``, text: $t(`skills.selectTeam`) },
                                    ...localTeams.map(team => ({
                                        value: team._id,
                                        text: team.name,
                                    })),
                                ]"
                            />
                        </div>
                        <div class="col-md-3">
                            <l-select
                                v-model="comparator"
                                wrapper-class="dropdown"
                                :options="[
                                    {
                                        value: 'order',
                                        text: $t('skills.option-sortByDefault'),
                                    },
                                    {
                                        value: 'categoryName',
                                        text: $t('skills.option-sortByCategory'),
                                    },
                                    {
                                        value: 'type',
                                        text: $t('skills.option-sortByType'),
                                    },
                                ]"
                            />
                        </div>
                    </div>

                    <div v-if="view === 'list'">
                        <hr />
                        <draggable
                            v-model="skills"
                            :disabled="comparator !== 'order' || teamFilter !== ''"
                        >
                            <transition-group>
                                <div
                                    v-for="skill in sortedSkills"
                                    :key="skill._id"
                                    class="row skill"
                                    data-test-class="skill-item"
                                >
                                    <div class="col-md-10 col-xs-10 skill-item">
                                        <div
                                            class="skill-title fake-link clickable"
                                            tabindex="0"
                                            @click="() => openEditModal(skill._id)"
                                            @keyup.enter="() => openEditModal(skill._id)"
                                        >
                                            <strong>{{ skill.name }}</strong>
                                        </div>

                                        <div class="color-grey">
                                            <span>
                                                <i class="fal fa-tags r-default-margin-s"></i>
                                                {{ skill.categoryName }}
                                            </span>
                                        </div>

                                        <div>
                                            <span
                                                v-show="skill.type === 'team'"
                                                class="color-success"
                                            >
                                                <i class="fal fa-users r-default-margin-s"></i>
                                                {{ $t("skills.tag-general") }}
                                            </span>
                                            <span
                                                v-for="lTeam in skill.localTeam"
                                                v-show="skill.type === 'localTeam'"
                                                :key="lTeam._id"
                                                class="color-primary r-default-margin-s"
                                            >
                                                <i class="fal fa-users r-default-margin-s"></i>
                                                {{ lTeam.name }}
                                            </span>
                                        </div>
                                    </div>

                                    <div
                                        :class="{
                                            'operation-hide-move': comparator,
                                            operation: !comparator,
                                        }"
                                    >
                                        <span
                                            class="l-default-margin color-primary tw-cursor-pointer"
                                            tabindex="0"
                                            @click="() => openEditModal(skill._id)"
                                            @keyup.enter="() => openEditModal(skill._id)"
                                        >
                                            <i class="fal fa-pencil"></i>
                                        </span>
                                        <span
                                            data-test-class="remove"
                                            class="l-default-margin color-primary tw-cursor-pointer"
                                            tabindex="0"
                                            @click="() => openRemoveModal(skill._id)"
                                            @keyup.enter="() => openRemoveModal(skill._id)"
                                        >
                                            <i class="fal fa-trash"></i>
                                        </span>

                                        <l-draggable-icon
                                            v-if="comparator === 'order' && teamFilter === ''"
                                            class="l-default-margin"
                                        />
                                    </div>
                                </div>
                            </transition-group>
                        </draggable>
                    </div>

                    <div v-else>
                        <l-skill-matrix
                            :matrix="sortedMatrix"
                            :custom-roles="customRoles"
                            :admin-mode="true"
                        >
                        </l-skill-matrix>
                    </div>

                    <l-modal
                        v-show="showModal"
                        size="m"
                        :close-on-backdrop-click="false"
                        :show-close-btn-header="showCloseBtnHeader"
                        @close="goBack"
                    >
                        <template #header>{{ modalTitle }}</template>
                        <template #body>
                            <router-view :close="goBack"></router-view>
                        </template>
                    </l-modal>
                </l-card-content>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import Draggable from "vuedraggable";
import orderBy from "lodash.orderby";

import LCardContent from "@/components/LCardContent";
import LDraggableIcon from "@/components/LDraggableIcon";
import LHeadline from "@/components/LHeadline";
import LModal from "@/components/LModal";
import LSelect from "@/components/LSelect";
import LSideMenuPicture from "@/components/LSideMenuPicture";
import LText from "@/components/LText";
import LSkillMatrix from "@/components/LSkillMatrix";

import icon from "@/img/icons/settings.png";

export default {
    components: {
        Draggable,
        LCardContent,
        LDraggableIcon,
        LHeadline,
        LModal,
        LSelect,
        LSideMenuPicture,
        LText,
        LSkillMatrix,
    },
    data() {
        return {
            modalTitle: "",
            error: false,
            showModal: false,
            showCloseBtnHeader: false,
            comparator: "order",
            teamFilter: "",
            view: "list",
            skills: [],
            matrix: [],
            customRoles: [],
        };
    },
    computed: {
        ...mapGetters({
            getSkills: "skills/getSkills",
            getMatrix: "skills/getMatrix",
            getCustomRoles: "skills/getCustomRoles",
            localTeams: "teams/local/listSortedByName",
        }),
        sortedSkills: function() {
            var skills = this.getSkills();

            // filter

            if (this.teamFilter != "") {
                skills = skills.filter(
                    s => s.type == "team" || s.localTeam.some(l => l._id == this.teamFilter),
                );
            }

            // return live order if currently editing, otherwise saved order

            if (this.view === "list" && this.comparator === "order") {
                return skills;
            } else {
                return orderBy(skills, this.comparator);
            }
        },
        sortedMatrix: function() {
            var matrix = this.getMatrix();

            // filter

            if (this.teamFilter != "") {
                matrix = matrix.filter(
                    s => s.type == "team" || s.localTeam.some(l => l._id == this.teamFilter),
                );
            }

            return orderBy(matrix, this.comparator);
        },
        tabs: function() {
            return [
                {
                    label: this.$t("skills.labelList"),
                    handler: this.showList,
                    show: true,
                    active: Boolean(this.view === "list"),
                },
                {
                    label: this.$t("skills.labelMatrix"),
                    handler: this.showMatrix,
                    show: true,
                    active: Boolean(this.view === "matrix"),
                },

                {
                    tabClass: "space",
                    show: true,
                },

                {
                    label: this.$t("usersTeams.tabsManageTeams"),
                    show: true,
                    handler: this.showTeams,
                    tabClass: "tw-ml-4",
                    testId: "manage-teams",
                    isButton: true,
                },
                {
                    label: this.$t("usersTeams.tabsManageLevels"),
                    show: true,
                    handler: this.showLevels,
                    tabClass: "tw-ml-4",
                    testId: "manage-levels",
                    isButton: true,
                },
                {
                    label: this.$t("skills.tabsManageCategories"),
                    show: true,
                    handler: this.showCategories,
                    tabClass: "tw-ml-4",
                    testId: "manage-categories",
                    isButton: true,
                },
                {
                    label: this.$t("skills.btn-create"),
                    show: true,
                    handler: this.openCreateModal,
                    tabClass: "tw-ml-4 btn-primary",
                    testId: "skill.create",
                    isButton: true,
                },
            ];
        },
    },
    watch: {
        $route: {
            immediate: true,
            handler(newRoute) {
                if (newRoute.name === "Skills") {
                    // relevant if user navigates back
                    this.showModal = false;
                } else if (newRoute.meta.modal) {
                    this.showModal = true;

                    switch (newRoute.name) {
                        case "SkillEdit":
                            this.showCloseBtnHeader = false;
                            this.modalTitle = this.$i18n.t(`skills.modal.title.SkillEdit`);
                            break;
                        case "SkillRemove":
                            this.showCloseBtnHeader = true;
                            this.modalTitle = this.$i18n.t(`skills.modal.title.SkillRemove`);
                            break;
                        case "SkillCreate":
                            this.showCloseBtnHeader = false;
                            this.modalTitle = this.$i18n.t(`skills.modal.title.SkillCreate`);
                            break;
                    }
                }

                // Keep the list of skills up to date
                this.skills = this.getSkills();
                this.matrix = this.getMatrix();
                this.customRoles = this.getCustomRoles();
            },
        },
        skills: {
            handler(newValue) {
                // If all skills are already in order, break of the watcher loop
                if (newValue.every((skill, order) => skill.order === order)) {
                    return;
                }

                // Optimistic update of skills to have a snappy UI
                this.skills = newValue.map((skill, order) => ({ ...skill, order }));

                // Dispatch
                this.$store.dispatch({
                    type: "skills/updateOrder",
                    payload: { skills: this.skills },
                });
            },
        },
        teamFilter: {
            handler(team) {
                this.comparator = "type";

                if (team != "") {
                    this.customRoles = this.getCustomRoles().filter(
                        customRole =>
                            !customRole.localTeams ||
                            !customRole.localTeams.length ||
                            customRole.localTeams.some(localTeam => localTeam == team),
                    );
                } else {
                    this.customRoles = this.getCustomRoles().filter(
                        customRole => customRole.showByDefault,
                    );
                }
            },
        },
    },
    created() {
        this.icon = icon;
    },
    methods: {
        openRemoveModal(id) {
            this.showModal = true;
            this.showCloseBtnHeader = true;
            this.modalTitle = this.$i18n.t(`skills.modal.title.SkillRemove`);
            this.$router.push(`/skills/remove/${id}`);
        },
        openEditModal(id) {
            this.showModal = true;
            this.showCloseBtnHeader = false;
            this.modalTitle = this.$i18n.t(`skills.modal.title.SkillEdit`);
            this.$router.push(`/skills/edit/${id}`);
        },
        openCreateModal() {
            this.showModal = true;
            this.showCloseBtnHeader = false;
            this.modalTitle = this.$i18n.t(`skills.modal.title.SkillCreate`);
            this.$router.push(`/skills/create`);
        },
        showTeams() {
            this.showModal = true;
            this.showCloseBtnHeader = true;
            this.modalTitle = this.$i18n.t(`skills.modal.title.SkillsManageTeams`);
            this.$router.push("/skills/teams/list");
        },
        showCategories() {
            this.showModal = true;
            this.showCloseBtnHeader = true;
            this.modalTitle = this.$i18n.t(`skills.modal.title.SkillsManageCategories`);
            this.$router.push("/skills/categories/list");
        },
        showLevels() {
            this.showModal = true;
            this.showCloseBtnHeader = true;
            this.modalTitle = this.$i18n.t(`skills.modal.title.SkillsManageLevels`);
            this.$router.push("/skills/levels/list");
        },
        goBack() {
            this.showModal = false;
            this.$router.push(`/skills`);
        },
        showList() {
            this.showModal = false;
            this.view = "list";
        },
        showMatrix() {
            this.showModal = false;
            this.view = "matrix";
        },
    },
};
</script>

<style scoped>
span.team {
    padding: 5px 8px;
    margin-right: 8px;
    display: inline-block;
}
div.skill {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 16px; /* this padding will make sure all the item has the same space and won't change base on content */
}

div.skill > div.skill-item {
    min-height: 64px; /* 80 - 16 it was 80 before, but we are add a padding so it won't affected by the content. */
    display: flex;
    align-items: flex-start;
    flex-direction: column;
}

div.skill div.skill-item > span {
    overflow-wrap: anywhere;
}

div.operation {
    margin-left: auto;
    width: 116px;
}
div.operation-hide-move {
    margin-left: auto;
    width: 120px;
}
.operation span {
    cursor: pointer;
}
</style>
